import type {
  CalendarAttendee,
  CalendarEventsResponse,
  CalendarStatus,
  CreateConversationRequest,
  CreateConversationResponse,
  GetPersonasResponse,
  GetReplicaResponse,
  JoinMeetingRequest,
  JoinMeetingResponse,
} from '../shared/types';

export class ApiClient {
  private baseUrl = '/api';

  async getPersonas(): Promise<GetPersonasResponse> {
    const response = await fetch(`${this.baseUrl}/personas`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = (errorData as any).error || response.statusText;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async createConversation(
    params: CreateConversationRequest
  ): Promise<CreateConversationResponse> {
    const response = await fetch(`${this.baseUrl}/conversations`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = (errorData as any).error || response.statusText;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async deleteConversation(id: number): Promise<void> {
    const response = await fetch(`${this.baseUrl}/conversations/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });

    if (!response.ok && response.status !== 204) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any).error || response.statusText);
    }
  }

  async joinMeeting(params: Pick<JoinMeetingRequest, 'meetingUrl' | 'botName' | 'joinAt'>): Promise<JoinMeetingResponse> {
    const response = await fetch(`${this.baseUrl}/meeting/join`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(params),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = (errorData as any).error || response.statusText;
      throw new Error(errorMessage);
    }

    return response.json();
  }

  async getCalendarStatus(): Promise<CalendarStatus> {
    const response = await fetch(`${this.baseUrl}/calendar/status`, { credentials: 'include' });
    if (!response.ok) throw new Error('Failed to fetch calendar status');
    return response.json();
  }

  async getCalendarEvents(): Promise<CalendarEventsResponse> {
    const response = await fetch(`${this.baseUrl}/calendar/events`, { credentials: 'include' });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error || 'Failed to fetch calendar events');
    }
    return response.json();
  }

  async scheduleBotForEvent(
    eventId: string,
    botName: string,
    eventTitle: string,
    eventDescription?: string,
    eventMeetingUrl?: string,
    attendees?: CalendarAttendee[],
  ): Promise<{ botId: string }> {
    const response = await fetch(`${this.baseUrl}/calendar/events/${eventId}/bot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ botName, eventTitle, eventDescription, eventMeetingUrl, attendees }),
    });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error || 'Failed to schedule bot');
    }
    return response.json();
  }

  async removeBotFromEvent(eventId: string): Promise<void> {
    const response = await fetch(`${this.baseUrl}/calendar/events/${eventId}/bot`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok && response.status !== 204) {
      const err = await response.json().catch(() => ({}));
      throw new Error((err as any).error || 'Failed to remove bot');
    }
  }

  async disconnectCalendar(): Promise<void> {
    const response = await fetch(`${this.baseUrl}/calendar/disconnect`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok && response.status !== 204) {
      throw new Error('Failed to disconnect calendar');
    }
  }

  async getReplica(replicaId: string): Promise<GetReplicaResponse> {
    const response = await fetch(`${this.baseUrl}/replicas/${replicaId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage = (errorData as any).error || response.statusText;
      throw new Error(errorMessage);
    }

    return response.json();
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
